
import { useState, useEffect } from 'react';
import { useStore } from '@/lib/store';
import { useFeedback } from '@/hooks/useFeedback';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertTriangle, Search, Info } from 'lucide-react';
import type { FeedbackCategory, FeedbackItem } from '@/types/feedback';
import { FeedbackSuggestionList } from './FeedbackSuggestionList';
import { Link } from 'react-router-dom';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'feedback' | 'support';
}

// Simple debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export function FeedbackDialog({ open, onOpenChange, defaultTab = 'feedback' }: FeedbackDialogProps) {
  const athlete = useStore(state => state.athlete);
  const authUser = useStore(state => state.authUser);
  const supportEmail = authUser?.email?.trim() || '';

  const {
    submitFeedback,
    submitSupportTicket,
    searchFeedback,
    toggleVote,
    userVotes,
    fetchComments,
    addComment,
    comments,
    commentsLoading,
  } = useFeedback();

  // Mode: 'feedback' | 'support'
  const [mode, setMode] = useState<'feedback' | 'support'>('feedback');

  // Sync mode with defaultTab when dialog opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMode(defaultTab);
    }
  }, [open, defaultTab]);

  // Feedback State
  const [category, setCategory] = useState<FeedbackCategory>('feature_request');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showAuthor, setShowAuthor] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Support State
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');

  // Captcha State
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState({ a: 0, b: 0 });

  // Generate new captcha on open or mode change
  useEffect(() => {
    if (open && mode === 'support') {
      const timer = setTimeout(() => {
        setCaptchaChallenge({
          a: Math.floor(Math.random() * 10) + 1,
          b: Math.floor(Math.random() * 10) + 1
        });
        setCaptchaAnswer('');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, mode]);

  // Search state
  const [suggestions, setSuggestions] = useState<FeedbackItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedTitle = useDebounce(title, 500);

  const isCaptchaValid = parseInt(captchaAnswer) === (captchaChallenge.a + captchaChallenge.b);
  const isFeedbackValid = title.trim().length >= 3 && description.trim().length >= 10;
  const isSupportValid = supportSubject.trim().length >= 3 && supportMessage.trim().length >= 10 && isCaptchaValid;

  // Search effect
  useEffect(() => {
    const performSearch = async () => {
      if (mode === 'feedback' && debouncedTitle.length >= 3) {
        setIsSearching(true);
        const results = await searchFeedback(debouncedTitle);
        setSuggestions(results);
        setIsSearching(false);
      } else {
        setSuggestions([]);
      }
    };
    performSearch();
  }, [debouncedTitle, searchFeedback, mode]);

  const handleSubmitFeedback = async () => {
    if (!isFeedbackValid) return;
    setSubmitting(true);

    const success = await submitFeedback(
      { title: title.trim(), description: description.trim(), category, show_author: showAuthor },
      athlete.name
    );

    setSubmitting(false);
    if (success) {
      setTitle('');
      setDescription('');
      setCategory('feature_request');
      setShowAuthor(false);
      setSuggestions([]);
      onOpenChange(false);
    }
  };

  const handleContactSupport = async () => {
    if (!isSupportValid) return;
    setSubmitting(true);

    const success = await submitSupportTicket(
      supportSubject.trim(),
      supportMessage.trim(),
      supportEmail
    );

    setSubmitting(false);
    if (success) {
      setSupportSubject('');
      setSupportMessage('');
      setCaptchaAnswer('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle>How can we help?</DialogTitle>
          <DialogDescription>
            Share ideas with the community or contact us privately.
          </DialogDescription>
        </DialogHeader>

        {/* TABS */}
        <div className="flex gap-2 border-b mb-4">
          <button
            className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${mode === 'feedback' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
            onClick={() => setMode('feedback')}
          >
            Public Feedback
          </button>
          <button
            className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${mode === 'support' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
            onClick={() => setMode('support')}
          >
            Contact Support
          </button>
        </div>

        {mode === 'feedback' ? (
          <div className="space-y-4 py-2 animate-in fade-in zoom-in-95 duration-200">
            {/* Privacy Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <div className="text-xs text-yellow-800">
                <p className="font-bold mb-1">Public Visibility Notice</p>
                <p>
                  <strong>Do NOT include personal data</strong> (email, phone).
                  Requests are reviewed by admins and may be added to the <Link to="/roadmap" className="underline hover:text-yellow-900" onClick={() => onOpenChange(false)}>Public Roadmap</Link>. This is visible to everyone.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as FeedbackCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="bug_report">Bug Report</SelectItem>
                  <SelectItem value="general">General Feedback</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Title</Label>
                {isSearching && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking duplicates...</span>}
              </div>
              <div className="relative">
                <Input
                  placeholder="Brief summary (e.g., 'Strava Sync')"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  className={suggestions.length > 0 ? "border-amber-300 ring-amber-200 focus-visible:ring-amber-200" : ""}
                />
                <Search className="absolute right-3 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              {/* Suggestions List */}
              {suggestions.length > 0 && (
                <FeedbackSuggestionList
                  items={suggestions}
                  userVotes={userVotes}
                  onVote={toggleVote}
                  onAddComment={addComment}
                  onFetchComments={fetchComments}
                  comments={comments}
                  commentsLoading={commentsLoading}
                  athleteName={athlete.name}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe your idea or issue in detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={5000}
                rows={5}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border bg-slate-50/50">
              <div className="space-y-0.5">
                <Label className="text-sm">Show my name publicly</Label>
                <Switch checked={showAuthor} onCheckedChange={setShowAuthor} />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2 animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex gap-3 items-start">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-bold mb-1">Private Channel</p>
                <p>
                  This is a private support channel. Use this for billing, account issues, or sensitive data.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Your Email</Label>
              <Input value={supportEmail || 'Using your account email'} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">We'll reply to this address.</p>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Billing issue, Account help, etc."
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="How can we help you?"
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                rows={6}
              />
            </div>

            {/* Safety Check (Captcha) */}
            <div className="p-4 rounded-xl border bg-slate-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Safety Check</Label>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Bot Protection</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center h-10 px-4 bg-white border-2 border-slate-200 rounded-lg font-mono text-lg font-bold text-slate-700 shadow-sm">
                  {captchaChallenge.a} + {captchaChallenge.b} = ?
                </div>
                <Input
                  type="number"
                  placeholder="Result"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  className="w-24 text-center text-lg font-bold"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Just making sure you are an athlete, not a robot!
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {mode === 'feedback' ? (
            <Button
              onClick={handleSubmitFeedback}
              disabled={!isFeedbackValid || submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Feedback
            </Button>
          ) : (
            <Button
              onClick={handleContactSupport}
              disabled={!isSupportValid || submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send Message
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
